import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ErrorIcon from '@mui/icons-material/Error';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import {
  VideoStateNotice,
  VideoStateNoticeTone,
} from '../../../common/model/type/video-state-notice.type';
import {
  getFailureReasonDisplay,
  getStateNoticeTone,
} from '../../../common/utils/video-notice-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

type NoticeVariant = 'compact' | 'full';

interface OvVideoStateNoticeProps {
  notice: VideoStateNotice;
  variant?: NoticeVariant;
  children?: ReactNode;
}

const renderIcon = (tone: VideoStateNoticeTone) => {
  switch (tone) {
    case 'info':
      return <InfoOutlinedIcon aria-hidden="true" />;

    case 'warning':
      return <WarningIcon aria-hidden="true" />;

    case 'failure':
      return <ErrorIcon aria-hidden="true" />;

    default: {
      const unhandledTone: never = tone;

      return unhandledTone;
    }
  }
};

const OvVideoStateNotice: FC<OvVideoStateNoticeProps> = ({
  notice,
  variant = 'full',
  children = null,
}) => {
  const { t } = useTranslation();
  const isFull = variant === 'full';
  const tone = getStateNoticeTone(notice);

  const renderFailure = (reason: string | null) => {
    const display = getFailureReasonDisplay(reason);

    switch (display.kind) {
      case 'timeout':
        return <p>{t('videos.notices.failureTimeout')}</p>;

      case 'none':
        return <p>{t('videos.notices.failureNoReason')}</p>;

      case 'other':
        return (
          <>
            <p>{t('videos.notices.failureOther')}</p>
            {isFull && (
              <details>
                <summary>{t('videos.notices.technicalDetails')}</summary>
                <Detail>{display.detail}</Detail>
              </details>
            )}
          </>
        );

      default: {
        const unhandledDisplay: never = display;

        return unhandledDisplay;
      }
    }
  };

  const renderContent = () => {
    switch (notice.kind) {
      case 'placement':
        return <p>{t(`videos.placement.notices.${notice.notice}`)}</p>;

      case 'archivedRecord':
        return <p>{t('videos.notices.archivedRecord')}</p>;

      case 'processing':
        return <p>{t('videos.notices.processing')}</p>;

      case 'uploading':
        return <p>{t('videos.notices.keepTabOpen')}</p>;

      case 'uploadFailed':
        return <p>{t('videos.notices.uploadFailed')}</p>;

      case 'uploadNotFinished':
        return (
          <>
            <p>
              {t(
                notice.canDelete
                  ? 'videos.notices.uploadNotFinished'
                  : 'videos.notices.uploadNotFinishedReadOnly'
              )}
            </p>
            {isFull && (
              <Secondary>{t('videos.notices.noUploadHere')}</Secondary>
            )}
          </>
        );

      case 'transcodeFailed':
        return (
          <>
            {isFull && <Strong>{t('videos.notices.failureHeading')}</Strong>}
            {renderFailure(notice.reason)}
          </>
        );

      case 'replacementProcessing':
        return (
          <>
            <Strong>{t('videos.notices.replacementProcessing')}</Strong>
            {isFull && <p>{t('videos.notices.replacementProcessingBody')}</p>}
          </>
        );

      case 'replacementUploading':
        return (
          <p>
            {t('videos.notices.replacementUploading', {
              percent: notice.percent,
            })}
          </p>
        );

      case 'replacementFailed':
        return (
          <p>
            {t(
              isFull
                ? 'videos.notices.replacementFailedBody'
                : 'videos.notices.replacementFailed'
            )}
          </p>
        );

      case 'replacementUnavailable':
        return <p>{t('videos.notices.replacementUnavailable')}</p>;

      case 'replacesVideo':
        return (
          <>
            <p>
              {t('videos.notices.replaces')} {children}.
            </p>
            <p>
              {t(
                notice.takesOver
                  ? 'videos.notices.replacesTakeOver'
                  : 'videos.notices.replacesNotShown'
              )}
            </p>
          </>
        );

      case 'stagedAppears':
        return <p>{t('videos.notices.stagedAppears')}</p>;

      case 'stagedBatch':
        if (!notice.placedCount) {
          return <p>{t('videos.notices.stagedNoneShown')}</p>;
        }

        return (
          <>
            <p>
              {t('videos.notices.stagedPlaced', { count: notice.placedCount })}
            </p>
            {notice.unplacedCount > 0 && (
              <p>
                {t('videos.notices.stagedUnplaced', {
                  count: notice.unplacedCount,
                })}
              </p>
            )}
          </>
        );

      default: {
        const unhandledNotice: never = notice;

        return unhandledNotice;
      }
    }
  };

  return (
    <Notice data-tone={tone} $tone={tone} $isFull={isFull}>
      {renderIcon(tone)}
      <Body>
        {renderContent()}
        {notice.kind !== 'replacesVideo' && children}
      </Body>
    </Notice>
  );
};

export default OvVideoStateNotice;

const TONE_COLOURS: Record<VideoStateNoticeTone, string> = {
  info: Colours.OV_CHARCOAL,
  warning: Colours.OV_ERROR_ACCESSIBLE,
  failure: Colours.OV_ERROR_ACCESSIBLE,
};

const Notice = styled.div<{ $tone: VideoStateNoticeTone; $isFull: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  font-size: ${({ $isFull }) =>
    $isFull ? Variables.fontSizes.MEDIUM : Variables.fontSizes.SMALL};
  font-weight: ${({ $tone }) => ($tone === 'info' ? 'normal' : 'bold')};
  color: ${({ $tone }) => TONE_COLOURS[$tone]};

  .MuiSvgIcon-root {
    flex: none;
    width: 1em;
    height: 1em;
    margin-top: 0.125rem;
  }

  p {
    margin: 0;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  min-width: 0;
`;

const Strong = styled.p`
  && {
    font-weight: bold;
  }
`;

const Secondary = styled.p`
  && {
    color: ${Colours.OV_CHARCOAL};
    font-weight: normal;
  }
`;

const Detail = styled.p`
  && {
    margin-top: 0.25rem;
    color: ${Colours.OV_BASE};
    font-weight: normal;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;
